import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Loader2, Users, Mail, Search, Ticket, ChevronDown, ChevronUp, Inbox, Star, Copy, Send, Check } from 'lucide-react';
import { formatMinutes } from '../lib/time';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_LABELS = {
  prueba: 'Prueba',
  individual: 'Individual',
  pack5: 'Pack 5',
  pack10: 'Pack 10',
  dele: 'DELE',
  intensivo: 'Intensivo',
};

const PACK_NAMES = { pack5: 'Pack 5 clases', pack10: 'Pack 10 clases', dele_pack5: 'Bono DELE 5 clases' };

export default function AdminStudents({ onSessionExpired }) {
  const { token } = useAuth();
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [openEmail, setOpenEmail] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [busyEmail, setBusyEmail] = useState(null);

  useEffect(() => {
    const fetchStudents = async () => {
      try {
        const { data: reviewData } = await axios.get(`${API}/reviews`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setReviews(reviewData);
        const { data } = await axios.get(`${API}/students`, {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true,
        });
        setStudents(data);
      } catch (e) {
        if (e.response?.status === 401) onSessionExpired && onSessionExpired();
        else toast.error('Error al cargar los alumnos');
      } finally {
        setLoading(false);
      }
    };
    fetchStudents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const reviewOf = (email) =>
    reviews.find((r) => (r.email || '').toLowerCase() === (email || '').toLowerCase());

  const requestReview = async (student, sendEmail) => {
    setBusyEmail(student.email);
    try {
      const language = student.bookings?.[0]?.language || 'es';
      const { data } = await axios.post(`${API}/reviews/request`, {
        name: student.name, email: student.email, language, send_email: sendEmail,
      }, { headers: { Authorization: `Bearer ${token}` } });
      setReviews((prev) => [data, ...prev]);
      if (sendEmail) {
        toast.success(`Solicitud enviada a ${student.email}`);
      } else {
        try {
          await navigator.clipboard.writeText(data.review_url);
          toast.success('Enlace de reseña creado y copiado al portapapeles');
        } catch {
          toast.success('Enlace de reseña creado. Cópialo desde la pestaña Reseñas.');
        }
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo crear la solicitud');
    } finally {
      setBusyEmail(null);
    }
  };

  const copyReviewLink = async (review) => {
    try {
      await navigator.clipboard.writeText(review.review_url);
      toast.success('Enlace copiado');
    } catch {
      toast.error('No se pudo copiar el enlace');
    }
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return students;
    return students.filter(
      (s) => (s.name || '').toLowerCase().includes(q) || s.email.toLowerCase().includes(q)
    );
  }, [students, query]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-10 h-10 text-[#7DBDD9] animate-spin" />
      </div>
    );
  }

  return (
    <div>
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-6">
        <div>
          <h2 className="text-2xl font-bold text-[#4A4A4A]">Alumnos</h2>
          <p className="text-sm text-gray-500">
            {students.length} alumno{students.length === 1 ? '' : 's'} ·{' '}
            {students.filter((s) => s.active_pack).length} con pack activo
          </p>
        </div>
        <div className="relative w-full md:w-72">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Buscar por nombre o email"
            data-testid="students-search"
            className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-gray-200 focus:border-[#A8D8EA] focus:ring-2 focus:ring-[#A8D8EA]/30 outline-none text-sm"
          />
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-2xl border border-gray-100" data-testid="no-students">
          <Inbox className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">No hay alumnos que coincidan.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((s, index) => {
            const open = openEmail === s.email;
            return (
              <div key={s.email} data-testid={`student-card-${index}`} className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                <button
                  onClick={() => setOpenEmail(open ? null : s.email)}
                  data-testid={`student-toggle-${index}`}
                  className="w-full text-left p-5 flex flex-col md:flex-row md:items-center justify-between gap-4 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="w-10 h-10 bg-gradient-to-br from-[#A8D8EA] to-[#7DBDD9] rounded-full flex items-center justify-center text-white flex-shrink-0">
                      <Users className="w-5 h-5" />
                    </span>
                    <div>
                      <p className="font-bold text-[#4A4A4A]">{s.name || '—'}</p>
                      <p className="text-sm text-gray-500 flex items-center gap-1.5">
                        <Mail className="w-3.5 h-3.5" />{s.email}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 flex-wrap md:justify-end">
                    <span className="px-2.5 py-1 rounded-md text-xs font-bold bg-gray-100 text-gray-600" data-testid={`student-classes-${index}`}>
                      {s.total_classes} clase{s.total_classes === 1 ? '' : 's'}
                    </span>
                    {Object.entries(s.by_type).map(([k, n]) => (
                      <span key={k} className="px-2.5 py-1 rounded-md text-xs font-semibold bg-[#E8F5FA] text-[#7DBDD9]">
                        {TYPE_LABELS[k] || k} ×{n}
                      </span>
                    ))}
                    {s.cancelled_classes > 0 && (
                      <span className="px-2.5 py-1 rounded-md text-xs font-semibold bg-red-50 text-red-600">
                        {s.cancelled_classes} cancelada{s.cancelled_classes === 1 ? '' : 's'}
                      </span>
                    )}
                    {s.active_pack ? (
                      <span className="px-2.5 py-1 rounded-md text-xs font-bold bg-green-50 text-green-700 border border-green-200 flex items-center gap-1" data-testid={`student-pack-${index}`}>
                        <Ticket className="w-3.5 h-3.5" />
                        {PACK_NAMES[s.active_pack.pack_type]}: {s.active_pack.allow_half_hour
                          ? `${formatMinutes(s.active_pack.remaining_minutes)} por reservar`
                          : `${s.active_pack.remaining_sessions} libres`}
                      </span>
                    ) : s.packs.length > 0 ? (
                      <span className="px-2.5 py-1 rounded-md text-xs font-semibold bg-yellow-50 text-yellow-700 border border-yellow-200" data-testid={`student-pack-${index}`}>
                        Pack pendiente
                      </span>
                    ) : null}
                    {(() => {
                      const rv = reviewOf(s.email);
                      const label = !rv ? 'Reseña: no solicitada'
                        : rv.status === 'requested' ? 'Reseña: solicitada'
                        : rv.status === 'received' ? 'Reseña: pendiente de aprobar'
                        : rv.status === 'published' ? 'Reseña: publicada' : 'Reseña: rechazada';
                      const cls = !rv ? 'bg-gray-100 text-gray-500'
                        : rv.status === 'requested' ? 'bg-yellow-50 text-yellow-700 border border-yellow-200'
                        : rv.status === 'received' ? 'bg-[#E8F5FA] text-[#7DBDD9] border border-[#A8D8EA]'
                        : rv.status === 'published' ? 'bg-green-50 text-green-700 border border-green-200'
                        : 'bg-red-50 text-red-600 border border-red-200';
                      return (
                        <span className={`px-2.5 py-1 rounded-md text-xs font-semibold ${cls}`} data-testid={`student-review-status-${index}`}>
                          {label}
                        </span>
                      );
                    })()}
                    {open ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                  </div>
                </button>

                {open && (
                  <div className="px-5 pb-5 border-t border-gray-100 pt-4" data-testid={`student-detail-${index}`}>
                    {s.packs.length > 0 && (
                      <div className="mb-4">
                        <p className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-2">Packs</p>
                        <div className="space-y-2">
                          {s.packs.map((p) => (
                            <div key={p.id} className="flex flex-wrap items-center gap-2 text-sm">
                              <span className="font-semibold text-[#4A4A4A]">{PACK_NAMES[p.pack_type] || p.pack_type}</span>
                              <span className="text-gray-500">{p.allow_half_hour
                                ? `${formatMinutes(p.completed_minutes)} consumidos · ${formatMinutes(p.scheduled_minutes)} programados · ${formatMinutes(p.remaining_minutes)} por reservar`
                                : `${p.used_sessions}/${p.total_sessions} usadas · ${p.remaining_sessions} libres`}</span>
                              <span className={`px-2 py-0.5 rounded-md text-xs font-semibold ${
                                p.status === 'active' ? 'bg-green-50 text-green-700'
                                  : p.status === 'cancelled' ? 'bg-red-50 text-red-600' : 'bg-yellow-50 text-yellow-700'
                              }`}>
                                {p.status === 'active' ? 'Activo' : p.status === 'cancelled' ? 'Cancelado' : 'Pendiente de pago'}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    <p className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-2">Reseña</p>
                    <div className="mb-5">
                      {(() => {
                        const rv = reviewOf(s.email);
                        const busy = busyEmail === s.email;
                        if (!rv) {
                          return (
                            <div className="flex flex-wrap gap-2">
                              <button
                                onClick={() => requestReview(s, false)}
                                disabled={busy}
                                data-testid={`student-request-review-${index}`}
                                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#E8F5FA] hover:bg-[#A8D8EA]/40 text-[#7DBDD9] text-xs font-semibold transition-colors disabled:opacity-50"
                              >
                                {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Star className="w-3.5 h-3.5" />}
                                Solicitar reseña (copiar enlace)
                              </button>
                              <button
                                onClick={() => requestReview(s, true)}
                                disabled={busy}
                                data-testid={`student-request-review-email-${index}`}
                                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-600 text-xs font-semibold transition-colors disabled:opacity-50"
                              >
                                <Send className="w-3.5 h-3.5" />Solicitar y enviar por email
                              </button>
                            </div>
                          );
                        }
                        return (
                          <div className="space-y-2">
                            <p className="text-sm text-gray-600" data-testid={`student-review-detail-${index}`}>
                              Solicitada el {new Date(rv.requested_at).toLocaleDateString('es-ES', { day: '2-digit', month: 'long', year: 'numeric' })}
                              {rv.email_sent_at ? ' · enviada por email' : ''}
                              {rv.rating ? ` · ${rv.rating}/5` : ''}
                            </p>
                            {rv.text && (
                              <blockquote className="px-4 py-3 rounded-xl bg-[#E8F5FA] text-sm text-[#4A4A4A]">“{rv.text}”</blockquote>
                            )}
                            <div className="flex flex-wrap gap-2">
                              <button
                                onClick={() => copyReviewLink(rv)}
                                data-testid={`student-copy-review-${index}`}
                                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#E8F5FA] hover:bg-[#A8D8EA]/40 text-[#7DBDD9] text-xs font-semibold transition-colors"
                              >
                                <Copy className="w-3.5 h-3.5" />Copiar enlace
                              </button>
                              {rv.status === 'received' && (
                                <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-yellow-50 text-yellow-700 text-xs font-semibold">
                                  <Check className="w-3.5 h-3.5" />Apruébala en la pestaña Reseñas
                                </span>
                              )}
                            </div>
                          </div>
                        );
                      })()}
                    </div>

                    <p className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-2">Clases</p>
                    <div className="space-y-1.5">
                      {s.bookings.map((b) => (
                        <div key={b.id} className="flex flex-wrap items-center gap-2 text-sm">
                          <span className="font-semibold text-[#4A4A4A] w-40">{b.date} · {b.time}</span>
                          <span className="px-2 py-0.5 rounded-md text-xs font-semibold bg-[#E8F5FA] text-[#7DBDD9]">
                            {TYPE_LABELS[b.class_type] || b.class_type}
                          </span>
                          {b.pack_id && (
                            <span className="px-2 py-0.5 rounded-md text-xs font-semibold bg-purple-50 text-purple-600">del pack</span>
                          )}
                          {b.status === 'cancelled' && (
                            <span className="px-2 py-0.5 rounded-md text-xs font-semibold bg-red-50 text-red-600">cancelada</span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
